import React, { useRef, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { Group } from 'react-konva';

// ElementRenderer is passed via the _ElementRenderer prop to avoid circular imports
const GroupElementComponent = observer(({ element, isSelected, onTransformEnd, onDragEnd, _ElementRenderer }) => {
  const groupRef = useRef(null);

  const handleDragEnd = useCallback((e) => {
    const node = e.target;
    element.set({
      x: node.x(),
      y: node.y(),
    });
    if (onDragEnd) onDragEnd(e);
  }, [element, onDragEnd]);

  const handleTransformEnd = useCallback((e) => {
    const node = groupRef.current;
    if (!node) return;

    const scaleX = node.scaleX();
    const scaleY = node.scaleY();

    // Scale each child proportionally
    element.children.forEach((child) => {
      if (child.set) {
        const updates = {
          x: (child.x || 0) * scaleX,
          y: (child.y || 0) * scaleY,
        };
        if ('width' in child) updates.width = (child.width || 0) * scaleX;
        if ('height' in child) updates.height = (child.height || 0) * scaleY;
        if (child.type === 'text' && child.fontSize) {
          updates.fontSize = Math.round(child.fontSize * Math.max(scaleX, scaleY));
        }
        child.set(updates);
      }
    });

    // Update group position, rotation, dimensions
    element.set({
      x: node.x(),
      y: node.y(),
      rotation: node.rotation(),
      width: element.width * scaleX,
      height: element.height * scaleY,
    });

    // Reset scale on the Konva node
    node.scaleX(1);
    node.scaleY(1);

    if (onTransformEnd) onTransformEnd(e);
  }, [element, onTransformEnd]);

  if (!element.visible) return null;

  return (
    <Group
      ref={groupRef}
      id={element.id}
      name={element.name || 'group'}
      x={element.x}
      y={element.y}
      width={element.width}
      height={element.height}
      rotation={element.rotation}
      opacity={element.opacity}
      draggable={element.draggable !== false}
      onDragEnd={handleDragEnd}
      onTransformEnd={handleTransformEnd}
    >
      {_ElementRenderer && element.children.map((child) => (
        <_ElementRenderer
          key={child.id}
          element={child}
          isSelected={false}
          groupChild={true}
        />
      ))}
    </Group>
  );
});

GroupElementComponent.displayName = 'GroupElementComponent';
export default GroupElementComponent;
